"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero4Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    leftImage?: string;
    leftImageAltText?: string;
    rightTopImage?: string;
    rightTopImageAltText?: string;
    rightBottomImage?: string;
    rightBottomImageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero4 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero4Props) => {
  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero4"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 4"
    > 
      <div className="container"> 
        <div className="flex flex-col lg:flex-row gap-20 lg:gap-24">
          <div className="w-full lg:w-12/24 flex items-center">
            <div className="flex flex-col gap-6 text-center lg:text-left items-center lg:items-start">
              {contents?.badge && (
                <Badge variant="outline" bold>
                  {contents?.badge}
                </Badge>
              )}
              {contents?.title && (
                <h1>
                  {parse(contents.title)}
                </h1>
              )}
              {contents?.content && (
                <div className="mt-4 paragraph-lg">
                  {parse(contents?.content)}
                </div>
              )}
              {contents?.hasButton && (
                <div className="mt-2 w-full md:w-auto">
                  <div className="flex flex-col items-center gap-4 md:flex-row">
                    <Button
                      asChild
                      size='lg'
                      className="w-full md:w-auto md:m-auto lg:m-0"
                      variant={contents?.buttonStyle || "primary"}
                    >
                      <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                    </Button>
                    {contents?.secondaryButton && (
                      <Button
                        className="w-full md:w-auto md:m-auto lg:m-0"
                        variant={contents?.secondaryButtonStyle || "outline"}
                        asChild
                        size='lg'
                      >
                        <Link href={contents?.secondaryButtonLink ?? ""}> 
                          {contents?.secondaryButtonText}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {contents?.microCopy && (
                    <p className="mt-2 paragraph-sm text-muted-foreground">
                      {contents?.microCopy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full lg:w-12/24 flex items-center justify-center">
            <div className="grid w-full grid-cols-2 grid-rows-2 gap-4 lg:gap-8">
              <div className="col-[1_/_2] row-[1_/_3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {contents?.leftImage && (
                    <Image
                      src={contents?.leftImage}
                      alt={contents?.leftImageAltText || ''}
                      title={contents?.leftImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                      priority
                      fetchPriority="high"
                    />
                  )}
                </div>
              </div>
              <div className="col-[2_/_3] row-[1_/_2] aspect-[4/3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {contents?.rightTopImage && (
                    <Image
                      src={contents?.rightTopImage}
                      alt={contents?.rightTopImageAltText || ''}
                      title={contents?.rightTopImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                      priority
                      fetchPriority="high"
                    />
                  )}
                </div>
              </div>
              <div className="col-[2_/_3] row-[2_/_3] aspect-[4/3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {contents?.rightBottomImage && (
                    <Image
                      src={contents?.rightBottomImage}
                      alt={contents?.rightBottomImageAltText || ''}
                      title={contents?.rightBottomImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                      priority
                      fetchPriority="high"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero4 };
